
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { useNavigationPreferences } from "@/hooks/useNavigationPreferences";

interface SearchableRoute {
  label: string;
  path: string;
  category: string;
  fullPath: string;
}

export const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { preferences, isLoading } = useNavigationPreferences();
  
  // Filter routes based on navigation preferences (respects role and user settings)
  const searchableRoutes = useMemo(() => {
    if (!preferences?.items) return [];
    
    return preferences.items
      .filter(item => item.visible && item.path) // Only show visible items with paths
      .map(item => ({
        label: item.label,
        path: item.path,
        category: item.category || 'Other',
        fullPath: item.fullPath || item.label, // Use the hierarchical label path
      }));
  }, [preferences]);

  const handleSelect = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  // Group routes by their main category
  const groupedRoutes = searchableRoutes.reduce((acc, route) => {
    if (!acc[route.category]) {
      acc[route.category] = [];
    }
    acc[route.category].push(route);
    return acc;
  }, {} as Record<string, SearchableRoute[]>);

  return (
    <>
      <Button
        variant="outline"
        className="relative w-64 justify-start text-sm text-muted-foreground bg-card hover:bg-muted/50"
        onClick={() => setOpen(true)}
        disabled={isLoading}
      >
        <Search className="mr-2 h-4 w-4" />
        I'm looking for...
      </Button>
      
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search for pages and features..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          {Object.entries(groupedRoutes).map(([category, routes]) => (
            <CommandGroup key={category} heading={category}>
              {routes.map((route) => (
                <CommandItem
                  key={route.path}
                  value={`${route.fullPath} ${route.category}`}
                  onSelect={() => handleSelect(route.path)}
                  className="cursor-pointer"
                >
                  <span className="text-sm">
                    {route.fullPath.includes(' > ') 
                      ? route.fullPath.substring(route.fullPath.indexOf(' > ') + 3)
                      : route.fullPath}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
};
